import type {
  CreateOtherFeeParams,
  FeeBatchParams,
  OtherFeeListItem,
  OtherFeeListParams,
  OtherFeeOpLogItem,
  OtherFeeOpLogParams,
  OtherFeeWalletDetail,
  OtherFeeWalletDetailParams,
  ReverseOtherFeeParams,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { EXPORT_TASK_BIZ_CODE, exportTaskApi } from '#/api/modules/common'
import { requestClient } from '#/api/request'
import { formatDateRange } from '#/utils'

type OtherFeeExportParams =
  | OtherFeeListParams
  | PagingRequest<OtherFeeListParams>

interface BatchReqItem {
  amount: number
  currency?: string
  flowType?: string
  remark?: string
}

interface BatchReq {
  customerId: number
  items: BatchReqItem[]
}

export const otherFeeApi = defineApi('operation', ({ prefix }) => {
  function isOtherFeePagingParams(
    params: OtherFeeExportParams,
  ): params is PagingRequest<OtherFeeListParams> {
    return 'condition' in params
  }

  function getOtherFeeListParams(params: PagingRequest<OtherFeeListParams>) {
    return {
      orderNo: params.condition?.orderNo,
      customerId: params.condition?.customerId
        ? Number(params.condition?.customerId)
        : undefined,
      customerName: params.condition?.customerName,
      currency: params.condition?.currency,
      flowType: params.condition?.flowType,
      operator: params.condition?.operator,
      status: params.condition?.status,
      timeRange: formatDateRange(params.condition?.timeRange),
    }
  }

  function getOtherFeeList(params: PagingRequest<OtherFeeListParams>) {
    params = {
      pageIndex: params.pageIndex,
      pageSize: params.pageSize,
      condition: getOtherFeeListParams(params),
    }
    return requestClient
      .post(prefix('customerFlow/query'), params)
      .then((res) => {
        return {
          items:
            res?.list?.map((item: OtherFeeListItem) => ({
              id: item.id,
              orderNo: item.orderNo,
              accountId: item.accountId,
              accountName: item.accountName,
              customerId: item.customerId,
              customerName: item.customerName,
              flowTime: item.flowTime,
              flowType: item.flowType,
              transactionType: item.transactionType,
              amount: item.amount,
              currency: item.currency,
              walletCurrency: item.walletCurrency,
              walletDeductionAmount: item.walletDeductionAmount,
              walletDebitAmount: item.walletDebitAmount,
              prepaidPoolCurrency: item.prepaidPoolCurrency,
              prepaidPoolAmount: item.prepaidPoolAmount,
              status: item.status,
              fundTxNo: item.fundTxNo,
              remark: item.remark,
              operator: item.operator,
              deductionOperator: item.deductionOperator,
              canReverse: item.canReverse,
              reverseTime: item.reverseTime,
              reverseOperator: item.reverseOperator,
              reverseReason: item.reverseReason,
              rollbackTime: item.rollbackTime,
              rollbackOperator: item.rollbackOperator,
              rollbackReason: item.rollbackReason,
            })) || [],
          total: res?.total,
        }
      })
  }

  function createOtherFee(params: CreateOtherFeeParams) {
    return requestClient.post(prefix('customerFlow/createDebit'), {
      ...params,
      accountId: params.accountId?.trim(),
      customerName: params.customerName?.trim(),
    })
  }

  /**
   * 批量新增其他扣费。
   * POST /customerFlow/createDebitBatch
   */
  function createOtherFeeBatch(params: FeeBatchParams) {
    const request: BatchReq = {
      customerId: Number(params.customerId),
      items: params.items.map((item) => ({
        amount: Number(item.amount),
        currency: item.currency,
        flowType: item.flowType,
        remark: item.remark,
      })),
    }
    return requestClient.post(prefix('customerFlow/createDebitBatch'), request)
  }

  function reverseOtherFee(params: ReverseOtherFeeParams) {
    return requestClient.post(prefix('customerFlow/reverse'), params)
  }

  function getOtherFeeOpLogs(params: OtherFeeOpLogParams) {
    return requestClient
      .post(prefix('customerFlow/getOpLogs'), params)
      .then((res) => (Array.isArray(res) ? res : res?.list || []))
      .then((list: OtherFeeOpLogItem[]) =>
        list.map((item) => ({
          id: item.id,
          action: item.action,
          createdTime: item.createdTime,
          operator: item.operator,
          summary: item.summary,
          title: item.title,
          content: item.content,
        })),
      )
  }

  function exportOtherFee(params: OtherFeeExportParams) {
    const condition = isOtherFeePagingParams(params)
      ? getOtherFeeListParams(params)
      : params

    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_CUSTOMER_FLOW_EXPORT,
      {
        condition,
      },
    )
  }

  function normalizeWalletDetail(res?: OtherFeeWalletDetail) {
    return {
      totalAmount: res?.totalAmount,
      totalAvailableAmount: res?.totalAvailableAmount,
      totalFrozenAmount: res?.totalFrozenAmount,
      currencyEditable: res?.currencyEditable,
      currBalance: res?.currBalance,
      creditUsed: res?.creditUsed,
      creditLimit: res?.creditLimit,
      currency: res?.currency,
    }
  }

  function getWalletDetail(params: Partial<OtherFeeWalletDetailParams>) {
    return requestClient
      .post<OtherFeeWalletDetail>(prefix('customerFlow/getDetails'), {
        ...params,
        customerId:
          params.customerId === undefined
            ? undefined
            : Number(params.customerId),
      })
      .then(normalizeWalletDetail)
  }

  return {
    getOtherFeeList,
    exportOtherFee,
    createOtherFee,
    createOtherFeeBatch,
    reverseOtherFee,
    getOtherFeeOpLogs,
    getOtherFeeListParams,
    getWalletDetail,
  }
})
